/**
 * עזרים משותפים למודול הצעות מנו — סטטוסים, פורמט, הודעת וואטסאפ.
 */

export const MANO_STATUS_LABELS = {
  draft: "טיוטה",
  published: "נשלחה",
  viewed: "נפתחה",
  interested: "לקוח מעוניין",
  closed: "נסגרה",
  expired: "פג תוקף",
  revoked: "לינק בוטל",
  archived: "ארכיון",
};

export const MANO_STATUS_COLORS = {
  draft: "default",
  published: "blue",
  viewed: "cyan",
  interested: "green",
  closed: "purple",
  expired: "orange",
  revoked: "red",
  archived: "default",
};

export const MANO_CURRENCY_SYMBOLS = { ILS: "₪", EUR: "€", USD: "$" };

export const MANO_CABIN_CATEGORY_LABELS = {
  inside: "פנימי",
  outside: "חיצוני",
  suite: "סוויטה",
  other: "אחר",
};

/** שלבי תפעול תשלום למנו (אדמין מעדכן · סוכן רואה) */
export const MANO_SUPPLIER_OPS_LABELS = {
  payment_received: "התקבל תשלום",
  awaiting_mano_payment: "ממתין לתשלום למנו",
  paid_to_mano: "שולם למנו",
};

export const MANO_SUPPLIER_OPS_COLORS = {
  payment_received: "blue",
  awaiting_mano_payment: "orange",
  paid_to_mano: "green",
};

export const MANO_SUPPLIER_OPS_OPTIONS = [
  { value: "payment_received", label: MANO_SUPPLIER_OPS_LABELS.payment_received },
  { value: "awaiting_mano_payment", label: MANO_SUPPLIER_OPS_LABELS.awaiting_mano_payment },
  { value: "paid_to_mano", label: MANO_SUPPLIER_OPS_LABELS.paid_to_mano },
];

export function fmtManoDate(value) {
  if (!value) return "";
  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");
  if (parts.length !== 3) return raw;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

export function fmtManoDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fmtManoDate(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fmtManoMoney(value, currency = "ILS") {
  if (value == null || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  const symbol = MANO_CURRENCY_SYMBOLS[currency] || currency || "";
  const formatted = num.toLocaleString("he-IL", {
    maximumFractionDigits: num % 1 === 0 ? 0 : 2,
  });
  return `${symbol}${formatted}`;
}

/** תבנית הודעת הוואטסאפ ללקוח (URL encoding מתבצע ב-buildWhatsAppShareUrl). */
export function buildManoWhatsAppMessage({
  customerDisplayName,
  departureDate,
  destinationsText,
  nights,
  finalTotalPrice,
  currency,
  publicUrl,
  validUntil,
  agentName,
  agencyName,
}) {
  const lines = [];
  lines.push(`שלום ${customerDisplayName || ""},`.trim().replace(/,$/, customerDisplayName ? "," : ""));
  lines.push("");
  lines.push(`הכנתי עבורך הצעת מחיר להפלגת מנו ספנות בתאריך ${fmtManoDate(departureDate)}.`);
  lines.push("");
  if (destinationsText) lines.push(`מסלול: ${destinationsText}`);
  if (nights) lines.push(`משך: ${nights} לילות`);
  if (finalTotalPrice != null) {
    lines.push(`מחיר כולל: ${fmtManoMoney(finalTotalPrice, currency)}`);
  }
  lines.push("");
  lines.push("לצפייה בהצעה המלאה:");
  lines.push(publicUrl || "");
  if (validUntil) {
    lines.push("");
    lines.push(`ההצעה בתוקף עד ${fmtManoDate(validUntil)}.`);
  }
  lines.push("");
  if (agentName) lines.push(agentName);
  if (agencyName) lines.push(agencyName);
  return lines.join("\n").trim();
}

/** בניית הודעת וואטסאפ מתוך אובייקט הצעה (רשומת סוכן עם snapshot). */
export function buildManoWhatsAppMessageFromQuote(quote, publicUrl) {
  const snapshot = quote?.snapshot_json || quote?.preview_snapshot || {};
  const sailing = snapshot.sailing || {};
  const agent = snapshot.agent || {};
  return buildManoWhatsAppMessage({
    customerDisplayName:
      quote?.customer_display_name || quote?.customer_full_name || snapshot?.customer?.display_name,
    departureDate: sailing.departure_date || quote?.departure_date,
    destinationsText: sailing.destinations_text || quote?.destinations_text,
    nights: sailing.nights || quote?.nights,
    finalTotalPrice: quote?.final_total_price ?? snapshot?.pricing?.final_total_price,
    currency: quote?.currency || snapshot?.pricing?.currency,
    publicUrl: publicUrl || quote?.public_url,
    validUntil: quote?.valid_until || snapshot?.valid_until,
    agentName: agent.name,
    agencyName: agent.agency_name,
  });
}

export function buildManoCruisePaymentPath({ orderId, quoteId } = {}) {
  const params = new URLSearchParams();
  if (orderId != null && String(orderId).trim()) {
    params.set("orderId", String(orderId).trim());
  }
  if (quoteId != null && String(quoteId).trim()) {
    params.set("quoteId", String(quoteId).trim());
  }
  const qs = params.toString();
  return qs ? `/p/dashboard/cruise-payment?${qs}` : "/p/dashboard/cruise-payment";
}

export async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
      return true;
    } catch {
      return false;
    }
  }
}
